<template>
  <div
    class="r-input-with-dropdown"
    :class="{ 'has-value': !!model, 'not-any-selected': signAsObject.value === 'n/a', 'input-focus': isInputOnFocus }"
    @mouseover="isInputOnHover = true"
    @mouseout="isInputOnHover = false"
  >
    <q-input
      ref="input"
      v-model="model"
      outlined
      dense
      :placeholder="signAsObject.label"
      :readonly="signAsObject.value === 'n/a'"
    >
      <template #prepend>
        <SignIcon
          v-if="showSignIcon"
          class="full-height"
          :active="!isInputOnFocus && !!model"
          :type="signAsObject.value"
        />

        <div v-else class="triangle-wrapper full-height">
          <TriangleIcon class="cursor-pointer" />
        </div>

        <q-menu
          v-model="isMenuOpened"
          content-class="r-input-with-dropdown__menu"
          :content-style="{ boxShadow: 'none', marginTop: '2px !important' }"
        >
          <q-list>
            <q-item
              v-for="option of signOptions"
              :key="option.value"
              v-close-popup
              :active="signAsObject.value === option.value"
              clickable
              @click="$emit('sign-changed', option)"
            >
              <q-item-section>
                <Grid gaf="column" justify-content="start" gap="var(--r-space-sm)">
                  <SignIcon v-if="option.value !== 'n/a'" :type="option.value" active />
                  <span class="text-body-1 text-secondary-2">{{ option.label }}</span>
                </Grid>
              </q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </template>

      <template #append>
        <CloseIcon v-if="!!model || signAsObject.value === 'n/a'" @click="$emit('clear-filter')" />
      </template>
    </q-input>
  </div>
</template>

<script>
import SignIcon from '@/ui/icons/SignIcon'
import TriangleIcon from '@/ui/icons/TriangleIcon'
import CloseIcon from '@/ui/icons/CloseIcon'

const signOptions = [
  { value: 'equal-greater', label: 'equal or greater than' },
  { value: 'equal-smaller', label: 'equal or smaller than' },
  { value: 'equal', label: 'equal' },
  { value: 'n/a', label: 'N/A' }
]

export default {
  components: {
    SignIcon,
    TriangleIcon,
    CloseIcon
  },

  props: {
    value: { required: true },
    sign: {
      type: String,
      required: true,
      validator: prop => {
        const allowedValues = signOptions.map(sign => sign.value)
        return allowedValues.includes(prop)
      }
    }
  },

  data: () => ({
    isInputOnFocus: false,
    isInputOnHover: false,
    isMenuOpened: false
  }),

  computed: {
    model: {
      get() {
        return this.value
      },
      set(newVal) {
        this.$emit('input', newVal)
      }
    },

    signAsObject() {
      return signOptions.find(s => s.value === this.sign)
    },

    showSignIcon() {
      if (this.signAsObject.value === 'n/a') return false
      if (this.isInputOnHover) return false
      if (this.isInputOnFocus) return true
      if (this.isMenuOpened) return false
      if (!!this.model) return true
      return true
    }
  },

  created() {
    this.signOptions = signOptions
  },

  mounted() {
    this.input = this.$el.querySelector('input')
    this.input.addEventListener('focus', this.changeFocusState)
    this.input.addEventListener('blur', this.changeFocusState)
  },

  beforeDestroy() {
    this.input.removeEventListener('focus', this.changeFocusState)
    this.input.removeEventListener('blur', this.changeFocusState)
  },

  methods: {
    changeFocusState() {
      this.isInputOnFocus = !this.isInputOnFocus
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/shadows';

.r-input-with-dropdown__menu {
  .q-list {
    @extend .shadow2;
    border: none;
  }

  .q-item {
    min-height: calc(var(--r-space-xl) + 1px);
    height: calc(var(--r-space-xl) + 1px);
    color: var(--r-secondary-2);
    padding: 0 var(--r-space-sm);

    .q-focus-helper {
      display: none;
    }

    &:not(last-child) {
      border-bottom: 1px solid var(--r-white-2);
    }

    &:hover {
      background: var(--r-white-2);
    }
  }
}
</style>

<style lang="scss" scoped>
@import '~@/styles/mixins';

.r-input-with-dropdown {
  .triangle-wrapper {
    border-right: 1px solid var(--r-secondary-4);
  }

  @include q-field__control {
    height: var(--r-field-height);
    min-height: var(--r-field-height) !important;
    background: var(--r-white-1);
    border: 1px solid var(--r-white-5);
    padding: 0 !important;

    &:before,
    &:after {
      display: none !important;
    }

    .q-field__prepend,
    .q-field__append {
      height: 100%;
      padding: 0 !important;
    }

    .q-field__prepend {
      padding-right: var(--r-space-xs) !important;
    }

    &:hover {
      border: 1px solid var(--r-secondary-4);
    }

    input {
      @extend .text-body-1;
      color: var(--r-secondary-1);

      &::placeholder {
        @extend .text-caption-3;
        color: var(--r-grey-2) !important;
        opacity: 1 !important;
      }
    }
  }

  &.input-focus {
    @include q-field__control {
      input {
        color: var(--r-secondary-2);
      }
    }
  }

  &.has-value:not(.input-focus):not(:hover) {
    @include q-field__control {
      border: 1px solid var(--r-white-4);
    }
  }

  &.not-any-selected {
    @include q-field__control {
      input::placeholder {
        color: var(--r-secondary-2) !important;
      }
    }
  }
}
</style>

<docs>
### Input with signs dropdown

```jsx
<template>
  <RInputWithSigns v-model="model" />
</template>

<script>
  export default {
    data: () => ({
      model: ''
    })
  }
</script>
```
</docs>
